import { useCallback, useState } from 'react';
import HoldingDetailsFlyout from '@/components/flyouts/HoldingDetailsFlyout';
import type { HoldingViewModel } from '@/store/holdingsApi';

export interface HoldingFlyoutSeed {
  holdingId: number | null | undefined;
  name: string | null | undefined;
  accountName?: string | null;
}

const buildHoldingFromSeed = (
  holdingId: number,
  name: string | null | undefined,
  accountName: string | null | undefined,
): HoldingViewModel => ({
  HoldingId: holdingId,
  Name: name ?? '',
  AccountName: accountName ?? '',
  AccountId: 0,
  InstitutionName: '',
  InstitutionId: 0,
  Balance: 0,
  Quantity: 0,
  Price: 0,
  PercentOfAccount: 0,
  Classifications: '',
  WealthHoldingType: '',
  UpdatedDate: '',
  CreatedDate: '',
  HasTaxLots: false,
  CanEditCostBasis: false,
  WealthType: 0,
});

export function useHoldingDetailsFlyout(options?: { endDate?: string }) {
  const [selectedHolding, setSelectedHolding] =
    useState<HoldingViewModel | null>(null);

  const canOpenHolding = useCallback(
    (holdingId: number | null | undefined): holdingId is number =>
      typeof holdingId === 'number' &&
      Number.isFinite(holdingId) &&
      holdingId > 0,
    [],
  );

  const openHolding = useCallback(
    (seed: HoldingFlyoutSeed) => {
      const { holdingId } = seed;
      if (!canOpenHolding(holdingId)) return;
      setSelectedHolding(
        buildHoldingFromSeed(holdingId, seed.name, seed.accountName),
      );
    },
    [canOpenHolding],
  );

  const closeHoldingFlyout = useCallback(() => {
    setSelectedHolding(null);
  }, []);

  const holdingFlyout = selectedHolding ? (
    <HoldingDetailsFlyout
      open
      toggleDrawer={closeHoldingFlyout}
      holding={selectedHolding}
      endDate={options?.endDate}
    />
  ) : null;

  return {
    openHolding,
    canOpenHolding,
    closeHoldingFlyout,
    holdingFlyout,
    selectedHolding,
  };
}

export default useHoldingDetailsFlyout;
